import type { EngineInput, EngineResult, EstimateItem, EstimateUnit, ProfitLeak, TradeEngine } from './estimate.types';
import { createEstimateItem, formatNumber } from './estimateItemFactory';
import { resolveEstimateRate } from './estimate.rates';
import { calculatePercentageMoney, multiplyMoney } from './money';

const MIN_HOURS_PER_M2 = 0.2;
const MAX_HOURS_PER_M2 = 10;
const LARGE_PROJECT_AREA_M2 = 50;
const HIGH_RISK_BUFFER_FACTOR = 0.15;

const REGIONAL_MIN_HOURLY_RATE: Record<string, number> = {
  default: 45,
  mazowieckie: 50,
  mazovia: 50,
  'warminsko-mazurskie': 40,
  warminsko_mazurskie: 40,
  'warmian-masurian': 40,
  slaskie: 48,
  silesia: 48,
};

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function resultHaystack(result: EngineResult): string {
  return normalizeText(
    result.items
      .map((item) => `${item.id} ${item.label} ${item.category ?? ''}`)
      .join(' ')
  );
}

function suggestionHaystack(result: EngineResult): string {
  return normalizeText(
    (result.meta.materialSuggestions ?? [])
      .map((item) => `${item.id} ${item.label} ${item.category}`)
      .join(' ')
  );
}

function hasAny(haystack: string, keywords: string[]): boolean {
  return keywords.some((keyword) => haystack.includes(normalizeText(keyword)));
}

function createLeak(input: {
  id: string;
  label: string;
  message: string;
  value: number;
  quantity: number;
  unit: string;
  unitRate: number;
  calculation: string;
  severity?: ProfitLeak['severity'];
  suggestedItem?: EstimateItem;
}): ProfitLeak {
  return {
    id: input.id,
    type: 'custom',
    severity: input.severity ?? 'warning',
    label: input.label,
    message: input.message,
    value: input.value,
    quantity: input.quantity,
    unit: input.unit,
    unitRate: input.unitRate,
    calculation: input.calculation,
    aiAdded: false,
    dismissed: false,
    decisionStatus: 'recommended',
    suggestedItem: input.suggestedItem,
  };
}

function getTrade(input: EngineInput): TradeEngine {
  if (input.tradeEngine) return input.tradeEngine;
  if (input.trade === 'lazienka') return 'bathroom';
  if (input.trade === 'malarz') return 'painting';
  if (input.trade === 'ogolnobudowlany') return 'general';
  return 'painting';
}

function getWorkingArea(input: EngineInput, result: EngineResult): number {
  return input.floorArea ?? input.tilesAreaM2 ?? input.pavingAreaM2 ?? result.meta.totalWorkingArea ?? 0;
}

function getEstimatedHours(input: EngineInput, result: EngineResult): number {
  return (
    input.estimatedHours ??
    input.laborHours ??
    result.items
      .filter((item) => item.pricingMode === 'hourly' || item.unit === 'r-g')
      .reduce((sum, item) => sum + item.quantity, 0)
  );
}

function getRegionalMinimumHourlyRate(region?: string): number {
  const normalized = normalizeText(region ?? '');
  return REGIONAL_MIN_HOURLY_RATE[region ?? ''] ?? REGIONAL_MIN_HOURLY_RATE[normalized] ?? REGIONAL_MIN_HOURLY_RATE.default;
}

function detectGeometryHoursMismatch(input: EngineInput, result: EngineResult): ProfitLeak[] {
  const area = getWorkingArea(input, result);
  const hours = getEstimatedHours(input, result);

  if (area <= 0 || hours <= 0) {
    return [];
  }

  const hoursPerM2 = hours / area;

  if (hoursPerM2 < MIN_HOURS_PER_M2) {
    return [
      createLeak({
        id: 'geometry_hours_mismatch_low',
        label: 'Podejrzanie malo godzin',
        message: 'Godziny wygladaja za nisko do metrazu. Sprawdz przygotowanie, grunt, sprzatanie i poprawki, zanim wyslesz wycene.',
        value: 0,
        quantity: hours,
        unit: 'r-g',
        unitRate: 0,
        calculation: `${formatNumber(hours)} r-g / ${formatNumber(area)} m2 = ${formatNumber(hoursPerM2)} r-g/m2`,
      }),
    ];
  }

  if (hoursPerM2 > MAX_HOURS_PER_M2) {
    return [
      createLeak({
        id: 'geometry_hours_mismatch_high',
        label: 'Podejrzanie duzo godzin',
        message: 'Godziny wygladaja bardzo wysoko do metrazu. Sprawdz, czy zakres nie jest zdublowany albo czy to faktycznie robota godzinowa.',
        value: 0,
        quantity: hours,
        unit: 'r-g',
        unitRate: 0,
        calculation: `${formatNumber(hours)} r-g / ${formatNumber(area)} m2 = ${formatNumber(hoursPerM2)} r-g/m2`,
      }),
    ];
  }

  return [];
}

function detectLowHourlyRate(input: EngineInput, result: EngineResult): ProfitLeak[] {
  const minRate = getRegionalMinimumHourlyRate(input.region);
  const hourlyItems = result.items.filter((item) => item.pricingMode === 'hourly' || item.unit === 'r-g');
  const implicitHours = input.estimatedHours ?? input.laborHours;
  const implicitRate = input.hourlyRate;

  const leaks = hourlyItems
    .filter((item) => item.unitPrice > 0 && item.unitPrice < minRate)
    .map((item) => {
      const delta = minRate - item.unitPrice;
      const value = multiplyMoney(item.quantity, delta);
      const calculation = `${formatNumber(item.quantity)} r-g x ${formatNumber(delta)} zl/r-g = ${formatNumber(value)} zl`;

      return createLeak({
        id: `low_hourly_rate_${item.id}`,
        label: 'Za niska roboczogodzina',
        message: `Stawka ${formatNumber(item.unitPrice)} zl/r-g jest ponizej minimum regionalnego ${formatNumber(minRate)} zl/r-g. Jesli to nie jest swiadoma promocja, oddajesz marze.`,
        value,
        quantity: item.quantity,
        unit: 'r-g',
        unitRate: delta,
        calculation,
        suggestedItem: createEstimateItem({
          id: `rate_guard_${item.id}`,
          label: 'Korekta stawki r-g do minimum regionalnego',
          category: 'rate_guard',
          quantity: item.quantity,
          unit: 'r-g',
          unitPrice: delta,
          pricingMode: 'hourly',
        }),
      });
    });

  if (leaks.length > 0 || !implicitHours || !implicitRate || implicitRate >= minRate) {
    return leaks;
  }

  const delta = minRate - implicitRate;
  const value = multiplyMoney(implicitHours, delta);

  return [
    createLeak({
      id: 'low_hourly_rate',
      label: 'Za niska roboczogodzina',
      message: `Stawka ${formatNumber(implicitRate)} zl/r-g jest ponizej minimum regionalnego ${formatNumber(minRate)} zl/r-g. Jesli to nie jest swiadoma promocja, oddajesz marze.`,
      value,
      quantity: implicitHours,
      unit: 'r-g',
      unitRate: delta,
      calculation: `${formatNumber(implicitHours)} r-g x ${formatNumber(delta)} zl/r-g = ${formatNumber(value)} zl`,
      suggestedItem: createEstimateItem({
        id: 'rate_guard',
        label: 'Korekta stawki r-g do minimum regionalnego',
        category: 'rate_guard',
        quantity: implicitHours,
        unit: 'r-g',
        unitPrice: delta,
        pricingMode: 'hourly',
      }),
    }),
  ];
}

function detectHighRiskBufferMissing(input: EngineInput, result: EngineResult): ProfitLeak[] {
  const riskText = normalizeText(String(input.riskLevel ?? ''));
  const highRisk = input.isHighRisk === true || input.highRisk === true || riskText === 'high' || riskText === 'wysokie';

  if (!highRisk || result.cost <= 0) {
    return [];
  }

  const haystack = `${resultHaystack(result)} ${normalizeText(input.scopeText ?? '')}`;
  if (hasAny(haystack, ['rezerwa', 'bufor', 'nieprzewidziane', 'risk_buffer'])) {
    return [];
  }

  const value = calculatePercentageMoney(result.cost, HIGH_RISK_BUFFER_FACTOR);

  return [
    createLeak({
      id: 'high_risk_buffer_missing',
      label: 'Brak bufora ryzyka',
      message: 'Projekt jest wysokiego ryzyka, ale nie widze rezerwy. Jesli wyjdzie niespodzianka, zaplacisz czasem i materialem z wlasnej kieszeni.',
      value,
      quantity: 1,
      unit: 'ryczalt',
      unitRate: value,
      calculation: `${formatNumber(result.cost)} zl x 15% = ${formatNumber(value)} zl`,
      suggestedItem: createEstimateItem({
        id: 'risk_buffer',
        label: 'Rezerwa na nieprzewidziane prace',
        category: 'risk_buffer',
        quantity: 1,
        unit: 'ryczałt' as EstimateUnit,
        unitPrice: value,
        pricingMode: 'flat',
      }),
    }),
  ];
}

function detectLargeProjectTransportMissing(input: EngineInput, result: EngineResult): ProfitLeak[] {
  const area = getWorkingArea(input, result);
  if (area <= LARGE_PROJECT_AREA_M2) {
    return [];
  }

  const haystack = `${resultHaystack(result)} ${normalizeText(input.scopeText ?? '')}`;
  if (hasAny(haystack, ['transport', 'wywoz', 'wywóz', 'urobek', 'spoil', 'dojazd'])) {
    return [];
  }

  const unitRate = resolveEstimateRate('transport_flat', input.region, input.userRates);

  return [
    createLeak({
      id: 'large_project_transport_missing',
      label: 'Brak transportu / wywozu',
      message: `Projekt ma ${formatNumber(area)} m2. Nie widze transportu ani wywozu. Jesli klient tego nie bierze na siebie, zostawiasz koszt po swojej stronie.`,
      value: unitRate,
      quantity: 1,
      unit: 'ryczalt',
      unitRate,
      calculation: `1 ryczalt x ${formatNumber(unitRate)} zl = ${formatNumber(unitRate)} zl`,
      suggestedItem: createEstimateItem({
        id: 'transport_flat',
        label: 'Transport / wywoz dla duzej roboty',
        category: 'transport',
        quantity: 1,
        unit: 'ryczałt' as EstimateUnit,
        unitPrice: unitRate,
        pricingMode: 'flat',
      }),
    }),
  ];
}

function detectMissingMaterialChecklist(input: EngineInput, result: EngineResult): ProfitLeak[] {
  const trade = getTrade(input);
  const scope = normalizeText(input.scopeText ?? '');
  const itemText = resultHaystack(result);
  const materialText = suggestionHaystack(result);
  const allText = `${itemText} ${materialText} ${scope}`;
  const area = Math.max(1, getWorkingArea(input, result));
  const leaks: ProfitLeak[] = [];

  const hasTilesScope = trade === 'bathroom' || trade === 'tiling' || hasAny(allText, ['plytki', 'glazura', 'gres', 'terakota']);
  if (hasTilesScope) {
    if (!hasAny(allText, ['fuga', 'grout'])) {
      const value = multiplyMoney(area, 15);
      leaks.push(createLeak({
        id: 'tile_materials_missing_grout',
        label: 'Brak fugi przy plytkach',
        message: 'Masz plytki, ale nie widze fugi. Jesli nie jest po stronie klienta, dolicz material i robote, bo to wroci na koncu.',
        value,
        quantity: area,
        unit: 'm2',
        unitRate: 15,
        calculation: `${formatNumber(area)} m2 x 15 zl/m2 = ${formatNumber(value)} zl`,
      }));
    }
    if (!hasAny(allText, ['klej', 'zaprawa', 'adhesive'])) {
      const value = multiplyMoney(area, 25);
      leaks.push(createLeak({
        id: 'tile_materials_missing_adhesive',
        label: 'Brak kleju do plytek',
        message: 'Masz ukladanie plytek, ale nie widze kleju ani zaprawy. Sprawdz, kto dostarcza material, zanim oddasz marze.',
        value,
        quantity: area,
        unit: 'm2',
        unitRate: 25,
        calculation: `${formatNumber(area)} m2 x 25 zl/m2 = ${formatNumber(value)} zl`,
      }));
    }
    if (!hasAny(allText, ['grunt', 'primer'])) {
      const value = multiplyMoney(area, 8);
      leaks.push(createLeak({
        id: 'tile_materials_missing_primer',
        label: 'Brak gruntu pod plytki',
        message: 'Przy plytkach nie widze gruntowania podloza. Jesli podloze nie jest gotowe, ryzykujesz poprawki.',
        value,
        quantity: area,
        unit: 'm2',
        unitRate: 8,
        calculation: `${formatNumber(area)} m2 x 8 zl/m2 = ${formatNumber(value)} zl`,
      }));
    }
  }

  const hasDrywallScope = trade === 'drywall' || hasAny(allText, ['gk', 'sucha zabudowa', 'sufit podwieszany', 'plyta gipsowa']);
  if (hasDrywallScope && !hasAny(allText, ['tasma', 'gips', 'szpachla', 'profil', 'wkrety', 'wkret'])) {
    const value = multiplyMoney(area, 15);
    leaks.push(createLeak({
      id: 'gk_materials_missing',
      label: 'Brak materialow GK',
      message: 'Przy GK nie widze tasmy, gipsu, profili ani wkretow. Jesli nie sa po stronie klienta, ta robota zje marze na detalach.',
      value,
      quantity: area,
      unit: 'm2',
      unitRate: 15,
      calculation: `${formatNumber(area)} m2 x 15 zl/m2 = ${formatNumber(value)} zl`,
    }));
  }

  const hasPaintingScope = trade === 'painting' || hasAny(allText, ['malowanie', 'gladz', 'szpachl']);
  if (hasPaintingScope) {
    if (!hasAny(allText, ['farba', 'paint']) && hasAny(allText, ['malowanie'])) {
      const value = multiplyMoney(area, 30);
      leaks.push(createLeak({
        id: 'painting_materials_missing_paint',
        label: 'Brak farby',
        message: 'Masz malowanie, ale nie widze farby na liscie materialow. Jesli klient jej nie kupuje, koszt zostaje u Ciebie.',
        value,
        quantity: area,
        unit: 'm2',
        unitRate: 30,
        calculation: `${formatNumber(area)} m2 x 30 zl/m2 = ${formatNumber(value)} zl`,
      }));
    }
    if (!hasAny(allText, ['akryl', 'zabezpieczenie', 'folia', 'tasma']) && area > 50) {
      const value = multiplyMoney(area, 4);
      leaks.push(createLeak({
        id: 'painting_materials_missing_protection',
        label: 'Brak zabezpieczenia i akrylu',
        message: 'Przy wiekszym malowaniu nie widze akrylu, folii ani tasm. Drobiazgi sa tanie tylko wtedy, gdy je doliczysz.',
        value,
        quantity: area,
        unit: 'm2',
        unitRate: 4,
        calculation: `${formatNumber(area)} m2 x 4 zl/m2 = ${formatNumber(value)} zl`,
      }));
    }
  }

  return leaks;
}

export function detectDomainProfitLeaks(input: EngineInput, result: EngineResult): ProfitLeak[] {
  return [
    ...detectGeometryHoursMismatch(input, result),
    ...detectLowHourlyRate(input, result),
    ...detectHighRiskBufferMissing(input, result),
    ...detectLargeProjectTransportMissing(input, result),
    ...detectMissingMaterialChecklist(input, result),
  ];
}

export function withDomainProfitLeaks(input: EngineInput, result: EngineResult): EngineResult {
  const existingIds = new Set(result.leaks.map((leak) => leak.id));
  const extraLeaks = detectDomainProfitLeaks(input, result).filter((leak) => !existingIds.has(leak.id));

  if (extraLeaks.length === 0) {
    return result;
  }

  return {
    ...result,
    leaks: [...result.leaks, ...extraLeaks],
  };
}
